import ContactsOverviewCardStateFull from '@modules/contacts/components/ContactsOverviewCardStateFull'
import { BIO_DEV_PHASE_ITEMS } from '@modules/search/components/ResultsSection/constants'

import { AnyProfile, ProfileType } from '../search/types'
import ConferenceAttendees from './ConferenceAttendees'

export const COMPANY_DISPLAYABLE_FIELDS = {
  description: 'description',
  assetsSummary: 'assetsSummary',
  partnerStrategy: 'partnerStrategy',
  clientPortfolio: 'clientPortfolio',
  corpHighlights: 'corpHighlights',
  anticipatedCorpHighlights: 'anticipatedCorpHighlights',
  attachment: 'attachment',
  attendees: 'attendees',
  sector: 'sector',
  subSectors: 'subSectors',
  primaryTherapeuticAreas: 'primaryTherapeuticAreas',
  numberOfEmployees: 'numberOfEmployees',
  phdNumber: 'phdNumber',
  founded: 'founded',
  sourceOfFoundation: 'sourceOfFoundation',
  ownershipState: 'ownershipState',
  addressLine1: 'addressLine1',
  addressLine2: 'addressLine2',
  country: 'country',
  city: 'city',
  zip: 'zip',
  state: 'state',
  website: 'website',
  linkedin: 'linkedin',
  facebook: 'facebook',
  twitter: 'twitter',
  investmentTimeframe: 'investmentTimeframe',
  investmentStage: 'investmentStage',
  primaryTickerSymbol: 'primaryTickerSymbol',
  primaryStockExchange: 'primaryStockExchange',
  secondaryListings: 'secondaryListings',
  ipo: 'ipo',
  additionalListingInfo: 'additionalListingInfo',
}

export const COMPANY_NON_EMPTY_STATE_FIELDS = [
  COMPANY_DISPLAYABLE_FIELDS.description,
  COMPANY_DISPLAYABLE_FIELDS.assetsSummary,
  COMPANY_DISPLAYABLE_FIELDS.partnerStrategy,
  COMPANY_DISPLAYABLE_FIELDS.clientPortfolio,
  COMPANY_DISPLAYABLE_FIELDS.corpHighlights,
  COMPANY_DISPLAYABLE_FIELDS.anticipatedCorpHighlights,
  COMPANY_DISPLAYABLE_FIELDS.attachment,
  COMPANY_DISPLAYABLE_FIELDS.attendees,
  COMPANY_DISPLAYABLE_FIELDS.sector,
  COMPANY_DISPLAYABLE_FIELDS.subSectors,
  COMPANY_DISPLAYABLE_FIELDS.primaryTherapeuticAreas,
  COMPANY_DISPLAYABLE_FIELDS.numberOfEmployees,
  COMPANY_DISPLAYABLE_FIELDS.phdNumber,
  COMPANY_DISPLAYABLE_FIELDS.founded,
  COMPANY_DISPLAYABLE_FIELDS.sourceOfFoundation,
  COMPANY_DISPLAYABLE_FIELDS.ownershipState,
  COMPANY_DISPLAYABLE_FIELDS.investmentTimeframe,
  COMPANY_DISPLAYABLE_FIELDS.investmentStage,
  COMPANY_DISPLAYABLE_FIELDS.primaryTickerSymbol,
  COMPANY_DISPLAYABLE_FIELDS.primaryStockExchange,
  COMPANY_DISPLAYABLE_FIELDS.secondaryListings,
  COMPANY_DISPLAYABLE_FIELDS.ipo,
  COMPANY_DISPLAYABLE_FIELDS.additionalListingInfo,
]

const COMPANY = {
  mainInfo: [
    {
      title: 'COMPANY_PROFILE_SECTION_SUMMARY',
      subSections: [
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.description,
        },
      ],
    },
    {
      title: 'COMPANY_PROFILE_SECTION_PRODUCTS',
      subSections: [
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.assetsSummary,
        },
      ],
    },
    {
      title: 'COMPANY_PROFILE_SECTION_COLLABORATIONS',
      subSections: [
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.partnerStrategy,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_STRATEGY',
        },
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.clientPortfolio,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_CLIENT_PORTFOLIO',
        },
      ],
    },
    {
      title: 'COMPANY_PROFILE_SECTION_ADDITIONAL_INFO',
      subSections: [
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.corpHighlights,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_HIGHLIGHTS',
        },
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.anticipatedCorpHighlights,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_ANTICIPATED_HIGHLIGHTS',
        },
      ],
    },
    {
      title: 'COMPANY_PROFILE_SECTION_ATTACHMENTS',
      subSections: [
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.attachment,
          isLink: true,
        },
      ],
    },
    {
      subSections: [
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.attendees,
          renderComponent: (props: { profileId: number }) => {
            return <ConferenceAttendees companyId={props.profileId} />
          },
        },
      ],
    },
  ],
  secondColumnInfo: [
    {
      title: 'COMPANY_PROFILE_SECTION_CATEGORIZATION',
      subSections: [
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.sector,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_MAIN_SECTOR',
        },
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.subSectors,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_SUBSECTORS',
          isList: true,
        },
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.primaryTherapeuticAreas,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_TERAPEUTIC_AREA',
          isList: true,
        },
      ],
    },
    {
      title: 'COMPANY_PROFILE_SECTION_COMPANY_DATA',
      subSections: [
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.numberOfEmployees,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_EMPLOYEES',
        },
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.phdNumber,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_PHDS_NUMBER',
        },
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.founded,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_FOUNDED_YEAR',
        },
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.sourceOfFoundation,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_FOUNDATION_SOURCE',
        },
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.ownershipState,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_OWNERSHIP_STATE',
        },
      ],
    },
  ],
  thirdColumnInfo: [
    {
      subSections: [
        {
          fieldName: 'contact_status',
          renderComponent: ({ profileData, isHighlighted }: { profileData: AnyProfile; isHighlighted: boolean }) => (
            <ContactsOverviewCardStateFull profile={profileData} isHighlighted={isHighlighted} />
          ),
        },
      ],
    },
    {
      title: 'COMPANY_PROFILE_SECTION_CONTACTS',
      subSections: [
        {
          joinedFieldsKey: 'fullAddress',
          joinedFieldsConfig: [
            {
              fieldName: COMPANY_DISPLAYABLE_FIELDS.addressLine1,
              joinSymbol: ', ',
            },
            {
              fieldName: COMPANY_DISPLAYABLE_FIELDS.addressLine2,
              joinSymbol: ', ',
            },
            {
              fieldName: COMPANY_DISPLAYABLE_FIELDS.zip,
              joinSymbol: ', ',
            },
            {
              fieldName: COMPANY_DISPLAYABLE_FIELDS.city,
              joinSymbol: ', ',
            },
            {
              fieldName: COMPANY_DISPLAYABLE_FIELDS.country,
              joinSymbol: ', ',
            },
            {
              fieldName: COMPANY_DISPLAYABLE_FIELDS.state,
              joinSymbol: ', ',
            },
            {
              fieldName: 'profileData.phone',
              joinSymbol: '',
              isBold: true,
              isNewLine: true,
            },
          ],
        },
        {
          fieldName: 'profileData.email',
          isEmail: true,
        },
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.website,
          isLink: true,
        },
        {
          joinedFieldsKey: 'socialLinks',
          joinedFieldsConfig: [
            {
              fieldName: COMPANY_DISPLAYABLE_FIELDS.linkedin,
              linkDomain: 'linkedin.com/in/',
              iconSrc: '/plus/assets/linkedin-icon.png',
              isLink: true,
            },
            {
              fieldName: COMPANY_DISPLAYABLE_FIELDS.facebook,
              linkDomain: 'facebook.com/',
              iconSrc: '/plus/assets/facebook-icon.png',
              isLink: true,
            },
            {
              fieldName: COMPANY_DISPLAYABLE_FIELDS.twitter,
              linkDomain: 'twitter.com/',
              iconSrc: '/plus/assets/twitter-icon.png',
              isLink: true,
            },
          ],
        },
      ],
    },
    {
      title: 'COMPANY_PROFILE_SECTION_INVESTMENT',
      subSections: [
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.investmentTimeframe,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_INVESTMENT_TIMEFRAME',
          isList: true,
        },
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.investmentStage,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_INVESTMENT_STAGE',
          isList: true,
        },
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.primaryTickerSymbol,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_TICKER_SYMBOL',
        },
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.primaryStockExchange,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_STOCK_EXCHANGE',
        },
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.secondaryListings,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_SECONDARY_LISTINGS',
        },
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.ipo,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_IPO',
        },
        {
          fieldName: COMPANY_DISPLAYABLE_FIELDS.additionalListingInfo,
          subTitle: 'COMPANY_PROFILE_SUBSECTION_ADDITIONAL_LISTING_INFO',
        },
      ],
    },
  ],
}

export const DELEGATE_DISPLAYABLE_FIELDS = {
  partneringObjectives: 'partneringObjectives',
  profileSummary: 'profileSummary',
  expertiseArea: 'expertiseArea',
  affiliations: 'affiliations',
  deals: 'deals',
  attachment: 'attachment',
  addressLine1: 'addressLine1',
  addressLine2: 'addressLine2',
  country: 'country',
  city: 'city',
  zip: 'zip',
  state: 'state',
  website: 'website',
  phone: 'sharedInfo.profilePhoneNumber',
  mobilePhone: 'sharedInfo.profileMobileNumber',
  email: 'sharedInfo.profilePrimaryEmail',
  secondaryEmail: 'sharedInfo.profileSecondaryEmail',
}

export const DELEGATE_NON_EMPTY_STATE_FIELDS = [
  DELEGATE_DISPLAYABLE_FIELDS.partneringObjectives,
  DELEGATE_DISPLAYABLE_FIELDS.profileSummary,
  DELEGATE_DISPLAYABLE_FIELDS.expertiseArea,
  DELEGATE_DISPLAYABLE_FIELDS.affiliations,
  DELEGATE_DISPLAYABLE_FIELDS.deals,
  DELEGATE_DISPLAYABLE_FIELDS.attachment,
]

const DELEGATE = {
  mainInfo: [
    {
      title: 'DELEGATE_PROFILE_SECTION_PARTNERING_OBJECTIVES',
      subSections: [
        {
          fieldName: DELEGATE_DISPLAYABLE_FIELDS.partneringObjectives,
        },
      ],
    },
    {
      title: 'DELEGATE_PROFILE_SECTION_PROF_BACKGROUND',
      subSections: [
        {
          fieldName: DELEGATE_DISPLAYABLE_FIELDS.profileSummary,
        },
      ],
    },
    {
      title: 'DELEGATE_PROFILE_SECTION_EXPERTISE_AREA',
      subSections: [
        {
          fieldName: DELEGATE_DISPLAYABLE_FIELDS.expertiseArea,
        },
      ],
    },
    {
      title: 'DELEGATE_PROFILE_SECTION_AFFILIATIONS',
      subSections: [
        {
          fieldName: DELEGATE_DISPLAYABLE_FIELDS.affiliations,
        },
      ],
    },
    {
      title: 'DELEGATE_PROFILE_SECTION_DEALS',
      subSections: [
        {
          fieldName: DELEGATE_DISPLAYABLE_FIELDS.deals,
        },
      ],
    },
    {
      title: 'DELEGATE_PROFILE_SECTION_ATTACHMENTS',
      subSections: [
        {
          fieldName: DELEGATE_DISPLAYABLE_FIELDS.attachment,
          isLink: true,
        },
      ],
    },
  ],
  secondColumnInfo: [
    {
      title: 'DELEGATE_PROFILE_SECTION_SHARED_CONTACT_INFO',
      subscriberAllowed: true,
      ecommerceAllowed: true,
      subSections: [
        {
          subTitle: 'DELEGATE_PROFILE_SECTION_SHARED_CONTACT_PHONE',
          joinedFieldsKey: 'fullPhone',
          joinedFieldsConfig: [
            {
              fieldName: DELEGATE_DISPLAYABLE_FIELDS.phone,
              isNewLine: true,
            },
            {
              fieldName: DELEGATE_DISPLAYABLE_FIELDS.mobilePhone,
              isNewLine: true,
            },
          ],
        },
        {
          subTitle: 'DELEGATE_PROFILE_SECTION_SHARED_CONTACT_EMAIL',
          joinedFieldsKey: 'fullEmail',
          joinedFieldsConfig: [
            {
              fieldName: DELEGATE_DISPLAYABLE_FIELDS.email,
              isNewLine: true,
              isEmail: true,
            },
            {
              fieldName: DELEGATE_DISPLAYABLE_FIELDS.secondaryEmail,
              isNewLine: true,
              isEmail: true,
            },
          ],
        },
      ],
    },
    {
      title: 'DELEGATE_PROFILE_SECTION_MAIN_INFO',
      subSections: [
        {
          subTitle: 'DELEGATE_PROFILE_SUBSECTION_ADDRESS',
          joinedFieldsKey: 'fullAddress',
          joinedFieldsConfig: [
            {
              fieldName: DELEGATE_DISPLAYABLE_FIELDS.addressLine1,
              joinSymbol: ', ',
            },
            {
              fieldName: DELEGATE_DISPLAYABLE_FIELDS.addressLine2,
              joinSymbol: ', ',
            },
            {
              fieldName: DELEGATE_DISPLAYABLE_FIELDS.city,
              joinSymbol: ', ',
            },
            {
              fieldName: DELEGATE_DISPLAYABLE_FIELDS.country,
              joinSymbol: ', ',
            },
            {
              fieldName: DELEGATE_DISPLAYABLE_FIELDS.state,
              joinSymbol: ', ',
            },
            {
              fieldName: DELEGATE_DISPLAYABLE_FIELDS.zip,
              joinSymbol: ', ',
            },
          ],
        },
        {
          fieldName: DELEGATE_DISPLAYABLE_FIELDS.website,
          subTitle: 'DELEGATE_PROFILE_SUBSECTION_WEBSITE',
          isLink: true,
        },
      ],
    },
  ],
}

export const ASSET_DISPLAYABLE_FIELDS = {
  devPhase: 'devPhase',
  differentiator: 'differentiator',
  categorySector: 'categorySector',
  categorySubSectors: 'categorySubSectors',
  ipRights: 'ipRights',
  clinicalStrategy: 'clinicalStrategy',
  serviceAvailability: 'serviceAvailability',
  attachment: 'attachment',
  description: 'description',
  keyPublications: 'keyPublications',
  partners: 'partners',
  url: 'url',
  moleculeType: 'moleculeType',
  modeOfAdministration: 'modeOfAdministration',
  orphanDiseaseIndication: 'orphanDiseaseIndication',
  primaryMarket: 'primaryMarket',
  marketingRightAvailableSought: 'marketingRightAvailableSought',
  fieldOfUseRestriction: 'fieldOfUseRestriction',
}

const BIOTECH = {
  mainInfo: [
    {
      title: 'ASSET_PRODUCT_DESCRIPTION',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.description,
          shouldParse: true,
        },
      ],
    },
    {
      title: 'ASSET_CLINICAL_STRATEGY',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.clinicalStrategy,
        },
      ],
    },
    {
      title: 'ASSET_KEY_PUBLICATIONS',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.keyPublications,
        },
      ],
    },
    {
      title: 'ASSET_IP_RIGHTS',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.ipRights,
        },
      ],
    },
    {
      title: 'ASSET_PARTNERS',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.partners,
        },
      ],
    },
    {
      title: 'ASSET_URL',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.url,
          isLink: true,
        },
      ],
    },
    {
      title: 'ASSET_ATTACHMENTS',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.attachment,
          isLink: true,
        },
      ],
    },
  ],
  secondColumnInfo: [
    {
      title: 'ASSET_CATEGORIZATION',
      subSections: [
        { subTitle: 'ASSET_MAIN_THERAPEUTIC_SECTOR', fieldName: ASSET_DISPLAYABLE_FIELDS.categorySector },
        { subTitle: 'ASSET_THERAPEUTIC_SUBSECTOR', fieldName: ASSET_DISPLAYABLE_FIELDS.categorySubSectors, isList: true },
        {
          subTitle: 'ASSET_DEVELOPMENT_PHASE',
          fieldName: ASSET_DISPLAYABLE_FIELDS.devPhase,
          isProgressBar: true,
          progressStage: BIO_DEV_PHASE_ITEMS,
        },
        { subTitle: 'ASSET_MOLECULE_TYPE', fieldName: ASSET_DISPLAYABLE_FIELDS.moleculeType },
        { subTitle: 'ASSET_MODE_OF_ADMINISTRATION', fieldName: ASSET_DISPLAYABLE_FIELDS.modeOfAdministration },
        { subTitle: 'ASSET_ORPHAN_DISEASE_INDICATION', fieldName: ASSET_DISPLAYABLE_FIELDS.orphanDiseaseIndication },
        { subTitle: 'ASSET_PRIMARY_MARKET', fieldName: ASSET_DISPLAYABLE_FIELDS.primaryMarket },
        { subTitle: 'ASSET_PARTNERING_STATUS', fieldName: ASSET_DISPLAYABLE_FIELDS.differentiator },
        { subTitle: 'ASSET_MARKETING_RIGHTS', fieldName: ASSET_DISPLAYABLE_FIELDS.marketingRightAvailableSought, isList: true },
      ],
    },
  ],
}
const MEDTECH = {
  mainInfo: [
    {
      title: 'ASSET_PRODUCT_DESCRIPTION',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.description,
          shouldParse: true,
        },
      ],
    },
    {
      title: 'ASSET_CLINICAL_STRATEGY',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.clinicalStrategy,
        },
      ],
    },
    {
      title: 'ASSET_KEY_PUBLICATIONS',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.keyPublications,
        },
      ],
    },
    {
      title: 'ASSET_IP_RIGHTS',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.ipRights,
        },
      ],
    },
    {
      title: 'ASSET_PARTNERS',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.partners,
        },
      ],
    },
    {
      title: 'ASSET_URL',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.url,
          isLink: true,
        },
      ],
    },
    {
      title: 'ASSET_ATTACHMENTS',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.attachment,
          isLink: true,
        },
      ],
    },
  ],
  secondColumnInfo: [
    {
      title: 'ASSET_CATEGORIZATION',
      subSections: [
        { subTitle: 'ASSET_SECTOR', fieldName: ASSET_DISPLAYABLE_FIELDS.categorySector },
        { subTitle: 'ASSET_SUBSECTOR', fieldName: ASSET_DISPLAYABLE_FIELDS.categorySubSectors, isList: true },
        { subTitle: 'ASSET_ORPHAN_DISEASE_INDICATION', fieldName: ASSET_DISPLAYABLE_FIELDS.orphanDiseaseIndication },
        { subTitle: 'ASSET_DEVELOPMENT_PHASE', fieldName: ASSET_DISPLAYABLE_FIELDS.devPhase },
        { subTitle: 'ASSET_PARTNERING_STATUS', fieldName: ASSET_DISPLAYABLE_FIELDS.differentiator },
        { subTitle: 'ASSET_MARKETING_RIGHTS', fieldName: ASSET_DISPLAYABLE_FIELDS.marketingRightAvailableSought, isList: true },
      ],
    },
  ],
}
const TECHNOLOGIES = {
  mainInfo: [
    {
      title: 'ASSET_TECHNOLOGY_DESCRIPTION',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.description,
          shouldParse: true,
        },
      ],
    },
    {
      title: 'ASSET_FIELD_OF_USE_RESTRICTIONS',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.fieldOfUseRestriction,
        },
      ],
    },
    {
      title: 'ASSET_CLINICAL_STRATEGY',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.clinicalStrategy,
        },
      ],
    },
    {
      title: 'ASSET_KEY_PUBLICATIONS',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.keyPublications,
        },
      ],
    },
    {
      title: 'ASSET_IP_RIGHTS',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.ipRights,
        },
      ],
    },
    {
      title: 'ASSET_PARTNERS',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.partners,
        },
      ],
    },
    {
      title: 'ASSET_URL',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.url,
          isLink: true,
        },
      ],
    },
    {
      title: 'ASSET_ATTACHMENTS',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.attachment,
          isLink: true,
        },
      ],
    },
  ],
  secondColumnInfo: [
    {
      title: 'ASSET_CATEGORIZATION',
      subSections: [
        { subTitle: 'ASSET_SECTOR', fieldName: ASSET_DISPLAYABLE_FIELDS.categorySector },
        { subTitle: 'ASSET_SUBSECTOR', fieldName: ASSET_DISPLAYABLE_FIELDS.categorySubSectors, isList: true },
        { subTitle: 'ASSET_PARTNERING_STATUS', fieldName: ASSET_DISPLAYABLE_FIELDS.differentiator },
        { subTitle: 'ASSET_MARKETING_RIGHTS', fieldName: ASSET_DISPLAYABLE_FIELDS.marketingRightAvailableSought, isList: true },
      ],
    },
  ],
}
const SERVICES = {
  mainInfo: [
    {
      title: 'ASSET_SERVICE_DESCRIPTION',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.description,
          shouldParse: true,
        },
      ],
    },
    {
      title: 'ASSET_URL',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.url,
          isLink: true,
        },
      ],
    },
    {
      title: 'ASSET_ATTACHMENTS',
      subSections: [
        {
          fieldName: ASSET_DISPLAYABLE_FIELDS.attachment,
          isLink: true,
        },
      ],
    },
  ],
  secondColumnInfo: [
    {
      title: 'ASSET_CATEGORIZATION',
      subSections: [
        { subTitle: 'ASSET_SERVICE_CATEGORY', fieldName: ASSET_DISPLAYABLE_FIELDS.categorySector },
        { subTitle: 'ASSET_SUBCATEGORIES', fieldName: ASSET_DISPLAYABLE_FIELDS.categorySubSectors, isList: true },
        { subTitle: 'ASSET_SERVICE_STATUS', fieldName: ASSET_DISPLAYABLE_FIELDS.differentiator },
        { subTitle: 'ASSET_SERVICE_AVAILABILITY', fieldName: ASSET_DISPLAYABLE_FIELDS.serviceAvailability, isList: true },
      ],
    },
  ],
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const PROFILE_VIEW_CONFIG: Record<ProfileType, any> = {
  [ProfileType.company]: COMPANY,
  [ProfileType.delegate]: DELEGATE,
  [ProfileType.biotech]: BIOTECH,
  [ProfileType.medtech]: MEDTECH,
  [ProfileType.technologies]: TECHNOLOGIES,
  [ProfileType.services]: SERVICES,
}

export const PROFILE_FOLLOWED_SECTIONS: Record<ProfileType, string[]> = {
  [ProfileType.biotech]: [ASSET_DISPLAYABLE_FIELDS.clinicalStrategy, ASSET_DISPLAYABLE_FIELDS.ipRights],
  [ProfileType.medtech]: [ASSET_DISPLAYABLE_FIELDS.clinicalStrategy, ASSET_DISPLAYABLE_FIELDS.ipRights],
  [ProfileType.technologies]: [ASSET_DISPLAYABLE_FIELDS.clinicalStrategy, ASSET_DISPLAYABLE_FIELDS.ipRights],
  [ProfileType.services]: [],
  [ProfileType.delegate]: [],
  [ProfileType.company]: [],
}

const ASSETS_SHARED_FOLLOWED_SUB_SECTIONS = [
  ASSET_DISPLAYABLE_FIELDS.categorySector,
  ASSET_DISPLAYABLE_FIELDS.categorySubSectors,
  ASSET_DISPLAYABLE_FIELDS.differentiator,
]

export const PROFILE_FOLLOWED_SUB_SECTIONS: Record<ProfileType, string[]> = {
  [ProfileType.company]: [
    COMPANY_DISPLAYABLE_FIELDS.sector,
    COMPANY_DISPLAYABLE_FIELDS.subSectors,
    COMPANY_DISPLAYABLE_FIELDS.primaryTherapeuticAreas,
    COMPANY_DISPLAYABLE_FIELDS.ipo,
    COMPANY_DISPLAYABLE_FIELDS.investmentTimeframe,
  ],
  [ProfileType.delegate]: [],
  [ProfileType.biotech]: [ASSET_DISPLAYABLE_FIELDS.devPhase, ...ASSETS_SHARED_FOLLOWED_SUB_SECTIONS],
  [ProfileType.medtech]: ASSETS_SHARED_FOLLOWED_SUB_SECTIONS,
  [ProfileType.technologies]: ASSETS_SHARED_FOLLOWED_SUB_SECTIONS,
  [ProfileType.services]: [
    ASSET_DISPLAYABLE_FIELDS.categorySector,
    ASSET_DISPLAYABLE_FIELDS.categorySubSectors,
    ASSET_DISPLAYABLE_FIELDS.serviceAvailability,
  ],
}
